import { useCallback, useEffect, useMemo, useState } from "react";
import { UserCheck, FilePlus2, Send, ShieldCheck, PlayCircle, Search, X } from "lucide-react";
import AppShell from "../shell/AppShell.jsx";
import WorkerRowAvatar from "./WorkerRowAvatar.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useAgentApplicantDetail } from "./useAgentApplicantDetail.js";
import { ApplicantDetailContent } from "./ApplicantDetailDrawer.jsx";
import { describeTrackingStatus, LINE_MEMBERSHIP_COPY } from "./applicantShared.js";
import { BuddyManagerSection } from "./buddyGuarantees/BuddyGuaranteeDrawerContent.jsx";
import { useEkycQueue, EKYC_QUEUE_PAGE_SIZE } from "../ekycReviews/useEkycQueue.js";
import EkycReviewDrawer from "../ekycReviews/EkycReviewDrawer.jsx";
import { getWorkerProfile, createWorkerProfileFromEkycReview } from "../workerProfiles/workerProfilesApi.js";
import { useReadyForMatchCandidatesList, READY_FOR_MATCH_DEFAULT_PAGE_SIZE } from "../readyForMatch/useReadyForMatchCandidatesList.js";
import { markReadyForMatch } from "../readyForMatch/readyForMatchApi.js";
import WorkerDetailedProfileDialog from "../workerDetailedProfile/WorkerDetailedProfileDialog.jsx";
import "./agentWorkers.css";
import "../pages/states.css";
import "./buddyGuarantees/buddyGuarantees.css";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Workers"];

/**
 * P162: the same screen serves two audiences, and the ONLY thing that differs
 * is who "these workers" are.
 *
 * The owner asked for the sysadmin side to be the Agent screen - "ให้เหมือนทุก
 * อย่างแต่ให้ข้อมูล scope เป็น sysadmin" - so this is deliberately one component
 * with two wordings rather than a copied page. A fork would drift: the Agent
 * side has been reshaped four times already (P157, R3, R4, R11), and every one
 * of those would have had to be done twice.
 *
 * Nothing about SCOPE is decided here. Every list on this page resolves
 * visibility server-side from the caller's own session, so a SuperAdmin sees the
 * whole platform and an Agent sees their line by virtue of who they are - this
 * object only makes the page SAY the right thing about what is on screen.
 */
const VARIANTS = {
  agent: {
    crumbs: CRUMBS,
    title: "Workers ของฉัน",
    subtitle: "ดูแลแรงงานในสายงานของคุณตามขั้นตอน ตั้งแต่ e-KYC จนพร้อมส่งจับคู่งาน",
    ekycHint:
      "แรงงานในสายงานของคุณที่ยังไม่พร้อมจับคู่งาน คลิกที่แถวไหนก็ได้เพื่อเปิดรายละเอียดของคนนั้น (และเข้าไปดู/แก้ไขโปรไฟล์แบบละเอียดได้จากตรงนั้น) ส่วนปุ่มทางขวาคือขั้นตอนถัดไปของแต่ละคน",
    readyEmpty: "ยังไม่มีแรงงานที่สร้างโปรไฟล์แล้วในสายงานของคุณ",
    // Every row is this Agent's own line, so naming it per row is noise.
    showOwningLine: false,
  },
  platform: {
    crumbs: ["Backoffice", "SuperAdmin", "Workers"],
    title: "Workers",
    subtitle: "ดูแลแรงงานทุกคนในระบบตามขั้นตอน ตั้งแต่ e-KYC จนพร้อมส่งจับคู่งาน",
    ekycHint:
      "แรงงานทุกสายงานในระบบที่ยังไม่พร้อมจับคู่งาน คลิกที่แถวไหนก็ได้เพื่อเปิดรายละเอียดของคนนั้น (และเข้าไปดู/แก้ไขโปรไฟล์แบบละเอียดได้จากตรงนั้น) ส่วนปุ่มทางขวาคือขั้นตอนถัดไปของแต่ละคน",
    readyEmpty: "ยังไม่มีแรงงานที่สร้างโปรไฟล์แล้วในระบบ",
    // Rows come from every line at once, so each one has to say whose it is.
    showOwningLine: true,
  },
};
const DRAWER_TITLE_ID = "aw-drawer-title";
const APPLICANT_DRAWER_TITLE_ID = "aw-applicant-drawer-title";
const REVIEW_DRAWER_TITLE_ID = "aw-review-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

// P157: the Workers page is a pipeline the Agent drives end to end. Each tab is
// one stage of the worker lifecycle, and every stage carries the real,
// Agent-callable action for it - e-KYC is no longer a "wait for the central
// team" black box (see the P157 backend change that let the owning Agent record
// the decision).
//
// P157-R3: the middle "Finalized Worker Profile" tab is gone at the owner's
// request. It was a whole tab for a single button that already exists in two
// better places: the e-KYC review drawer shows "ผ่าน e-KYC แล้ว - รอสร้าง Worker
// Profile" with the same real create action the moment a decision is recorded,
// and the row itself now carries it (see EkycRowAction). Nothing was dropped -
// see the KYC tab's own note for how its rows widened to absorb that stage.
const TABS = [
  { key: "ekyc", label: "KYC" },
  { key: "ready", label: "Ready for Match" },
];

const QUEUE_STATE_BADGE = {
  Waiting: "warning",
  InProgress: "info",
  Submitted: "info",
  NeedsCorrection: "warning",
  Approved: "success",
  Rejected: "danger",
};

const STATUS_BADGE_VARIANT = { Active: "success", Verified: "success", Pending: "warning", Suspended: "danger", Rejected: "danger" };

function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

/** Initials for the row avatar - first two chars for a single token (works for
 *  Thai/Bengali), first+last initial for a multi-word Latin name. */
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

// Prefer the backend's own Thai message (409 gate reasons, 400 validation) over
// a generic fallback - a mark-ready refusal names the missing items, and an
// e-KYC decision refusal explains the state conflict, both worth showing.
function describeActionError(error, fallback) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้สำหรับแรงงานรายนี้";
  }
  const serverMessage = error?.body?.message;
  if ((error?.status === 400 || error?.status === 409) && serverMessage) {
    return serverMessage;
  }
  return fallback;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 404) {
    return "ไม่พบข้อมูลแรงงานรายนี้";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูข้อมูลแรงงานรายนี้";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/agent/workers - P157: rebuilt from a flat two-list screen into a
 * lifecycle-tabbed console the Agent drives end to end. Reuses the real,
 * already-Agent-scoped e-KYC queue (P149) and Ready For Match candidate list
 * (P099) - no new read endpoint - and wires the real, now-Agent-callable
 * actions on top: start e-KYC, decide e-KYC, create Worker Profile, mark Ready
 * For Match. Visibility is still resolved entirely server-side from the
 * caller's session scope, so an Agent only ever sees and acts on their own line.
 */
export default function AgentWorkersPage({ variant = "agent" }) {
  const copy = VARIANTS[variant] ?? VARIANTS.agent;
  const isPlatform = variant === "platform";
  // The Agent Portal guard demands an owning Agent scope and refuses everyone
  // else - correct for /backoffice/agent/workers, and exactly wrong for the
  // sysadmin route, where the caller has a Platform scope and no agent id at
  // all. The platform variant still waits on identity (so the shell does not
  // flash) but never requires an agent.
  const agentGuard = useAgentPortalGuard();
  const guard = isPlatform && agentGuard.status === "forbidden" ? { ...agentGuard, status: "ready" } : agentGuard;
  const [tab, setTab] = useState("ekyc");
  // Each tab reports its own total once loaded, so the count pill fills in as a
  // tab is visited (only the active tab fetches, so counts are progressive - an
  // honest "known so far" rather than three eager requests on mount).
  const [counts, setCounts] = useState({});
  const reportCount = useCallback(
    (key, value) => setCounts((prev) => (prev[key] === value ? prev : { ...prev, [key]: value })),
    []
  );

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={copy.crumbs} guard={guard} />;
  }

  return (
    <AppShell crumbs={copy.crumbs}>
      <div className="wx-aw-page">
        <header className="wx-aw-head">
          <div>
            <h1>{copy.title}</h1>
            <p>{copy.subtitle}</p>
          </div>
          {isPlatform && (
            <span className="wx-aw-scope-pill">
              Scope <b>SuperAdmin</b>
            </span>
          )}
        </header>

        <div className="wx-aw-tabs" role="tablist" aria-label="ขั้นตอนของแรงงาน">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              className={`wx-aw-tab${tab === item.key ? " wx-aw-tab--active" : ""}`}
              onClick={() => setTab(item.key)}
            >
              {item.label}
              {counts[item.key] != null && <span className="wx-aw-tab-count">{counts[item.key]}</span>}
            </button>
          ))}
        </div>

        {tab === "ekyc" && <EkycTab onCount={(n) => reportCount("ekyc", n)} copy={copy} />}
        {tab === "ready" && <ReadyTab onCount={(n) => reportCount("ready", n)} copy={copy} />}
      </div>
    </AppShell>
  );
}

/**
 * P162: who this worker belongs to - rendered ONLY on the sysadmin variant.
 *
 * On the Agent screen every row is that Agent's own line, so naming it on each
 * row would repeat the same words down the page. On the sysadmin screen the
 * rows come from every line at once, and without this a platform-wide list is
 * four people with no way to tell whose they are - which is the one thing a
 * sysadmin needs from this screen that an Agent does not.
 */
function OwningLine({ item, copy }) {
  if (!copy.showOwningLine) {
    return null;
  }

  const agentName = item.agent?.displayName || item.agent?.agentCode;
  const plcName = item.line?.plcName;
  if (!agentName && !plcName) {
    return null;
  }

  return (
    <span className="wx-aw-queue-line">
      {agentName ?? "-"}
      {plcName ? ` · ${plcName}` : ""}
    </span>
  );
}

/** A debounced search input shared by every tab. */
function useDebouncedSearch() {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);
  return { input, setInput, search };
}

/**
 * P157-R11: the list toolbar - search on the left, "how many am I looking at"
 * on the right, on one bordered band directly above the rows.
 *
 * It used to be a bare input floating on its own row under a blue hint bar,
 * with nothing to its right and no visual tie to the list it filtered (the
 * owner's note: "วางได้แย่มาก"). A search field belongs to its list, so it now
 * sits on the list's own header band, with the leading magnifier that makes it
 * read as a search box at a glance and a clear button so getting back to the
 * full list is one click rather than a select-all-and-delete.
 */
function SearchBar({ input, onChange, placeholder, totalCount, shownCount }) {
  return (
    <div className="wx-aw-toolbar">
      <label className="wx-aw-search">
        <span className="wx-visually-hidden">{placeholder}</span>
        {/* A wrapper, not the bare <svg>: the tile needs its own box to paint,
            and sizing the svg itself would scale the glyph with it. */}
        <span className="wx-aw-search-icon" aria-hidden="true">
          <Search size={17} />
        </span>
        <input
          type="text"
          placeholder={placeholder}
          value={input}
          onChange={(event) => onChange(event.target.value)}
        />
        {input && (
          <button type="button" className="wx-aw-search-clear" onClick={() => onChange("")} aria-label="ล้างคำค้นหา">
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </label>
      {totalCount != null && (
        <span className="wx-aw-toolbar-count">
          {input ? (
            <>
              พบ <strong>{totalCount.toLocaleString("th-TH")}</strong> คน
            </>
          ) : (
            <>
              ทั้งหมด <strong>{totalCount.toLocaleString("th-TH")}</strong> คน
              {shownCount != null && totalCount > shownCount ? ` (แสดง ${shownCount})` : ""}
            </>
          )}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: KYC - every applicant in the Agent's line who is not yet a matchable
// worker: start e-KYC, decide it, and create the Worker Profile that follows.
// ---------------------------------------------------------------------------
function EkycTab({ onCount, copy = VARIANTS.agent }) {
  const { input, setInput, search } = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  // Two separate things a row can open:
  //   - clicking anywhere on the row -> the applicant's PROFILE detail (same as
  //     a Ready-for-Match row), a read view of who this person is, carrying the
  //     door to the editable detailed worker profile.
  //   - clicking the row's own action button -> the e-KYC REVIEW drawer, the
  //     action surface (documents, risk, decision).
  const [profileApplicationId, setProfileApplicationId] = useState(null);
  const [reviewApplicationId, setReviewApplicationId] = useState(null);
  const [reviewRow, setReviewRow] = useState(null);

  useEffect(() => setPage(1), [search]);

  // P157-R3: no `state` filter any more. It used to pin this list to the
  // "Pending" pseudo-state (still in e-KYC), which was correct only while the
  // create-profile tab existed to catch the rows that left it on approval. With
  // that tab gone, filtering here would strand an already-approved applicant who
  // has no Worker Profile yet - visible nowhere, actionable nowhere. Unfiltered,
  // this is a strict superset: each row still carries its own real queueState
  // badge and its own real nextAction, so an approved row shows "สร้าง Worker
  // Profile" right here and a rejected one shows its status with no action
  // (previously it just vanished, which was worse).
  const filters = useMemo(() => ({ page, pageSize: EKYC_QUEUE_PAGE_SIZE, search }), [page, search]);
  const { status, data, error } = useEkycQueue(filters, reloadToken);
  const applicantDetailState = useAgentApplicantDetail(profileApplicationId);

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / EKYC_QUEUE_PAGE_SIZE));

  useEffect(() => {
    if (data) onCount?.(totalCount);
  }, [data, totalCount, onCount]);

  // Keep the REVIEW drawer's row FRESH as the queue refetches. Without this, the
  // row captured at click time stays reviewRecordExists=false forever, so after
  // "เริ่ม e-KYC" starts a review the drawer never advances past the start panel
  // - it looks like nothing happened. When the row later leaves the pending list
  // (e.g. approved), we keep the last row so the drawer can still show its result.
  useEffect(() => {
    if (reviewApplicationId == null) {
      return;
    }
    const fresh = items.find((row) => row.workerApplicationId === reviewApplicationId);
    if (fresh) {
      setReviewRow(fresh);
    }
  }, [items, reviewApplicationId]);

  // Only ONE drawer may be open at a time: the review drawer's own
  // selectableSelector cannot close the profile drawer for us (a click on the
  // action button is inside .wx-aw-queue-row, which the profile drawer treats as
  // a legitimate "switch selection" click), so each opener closes the other.
  function openProfile(item) {
    setReviewApplicationId(null);
    setReviewRow(null);
    setProfileApplicationId(item.workerApplicationId);
  }

  function openReview(item) {
    setProfileApplicationId(null);
    setReviewApplicationId(item.workerApplicationId);
    setReviewRow(item);
  }

  function closeReview() {
    setReviewApplicationId(null);
    setReviewRow(null);
  }

  async function handleCreateProfile(item) {
    if (!item.ekycReviewId) {
      setActionError("ยังไม่มีเรคคอร์ด e-KYC สำหรับรายการนี้ จึงสร้าง Worker Profile ไม่ได้");
      return;
    }
    setBusyId(item.workerApplicationId);
    setActionError(null);
    try {
      await createWorkerProfileFromEkycReview(item.ekycReviewId);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setActionError(describeActionError(err, "สร้าง Worker Profile ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="wx-panel wx-aw-list-panel">
      <SearchBar
        input={input}
        onChange={setInput}
        placeholder="ค้นหาชื่อหรือเบอร์โทร"
        totalCount={data ? totalCount : null}
        shownCount={items.length}
      />

      <p className="wx-aw-tab-hint">{copy.ekycHint}</p>

      {actionError && <p className="wx-aw-action-error" role="alert">{actionError}</p>}

      {status === "error" && data === null ? (
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <p className="wx-state-body">{describeListError(error)}</p>
        </div>
      ) : status === "loading" && data === null ? (
        <PageSkeleton variant="table" label="กำลังโหลดรายการ..." />
      ) : items.length === 0 ? (
        <p className="wx-empty-list">ไม่มีแรงงานในขั้นตอนนี้</p>
      ) : (
        <>
          <ul className="wx-aw-queue">
            {items.map((item) => (
              <li
                key={item.workerApplicationId}
                className="wx-aw-queue-row wx-aw-queue-row--clickable"
                onClick={() => openProfile(item)}
              >
                <WorkerRowAvatar applicationId={item.workerApplicationId} initials={initials(item.fullName)} />
                {/* The whole row is the click target, but this stays a real
                    button so the row is still reachable and operable by
                    keyboard - the li's onClick alone would not be. */}
                <button type="button" className="wx-aw-queue-main" onClick={() => openProfile(item)}>
                  <span className="wx-aw-queue-name">{item.fullName || "-"}</span>
                  <span className="wx-aw-queue-sub">
                    {item.nationality || "-"}
                    {item.phoneNumber ? ` · ${item.phoneNumber}` : ""}
                  </span>
                  <OwningLine item={item} copy={copy} />
                </button>
                <span className={`wx-aw-status wx-aw-status--${QUEUE_STATE_BADGE[item.queueState] ?? "neutral"}`}>{item.queueStateLabel}</span>
                {/* The action is its own decision - a click here must never also
                    count as "open this person's detail". */}
                <div className="wx-aw-queue-side" onClick={(event) => event.stopPropagation()}>
                  <EkycRowAction
                    item={item}
                    onOpen={() => openReview(item)}
                    onCreateProfile={() => handleCreateProfile(item)}
                    busy={busyId === item.workerApplicationId}
                  />
                </div>
              </li>
            ))}
          </ul>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {/* Row click -> applicant profile detail (same view Ready for Match uses
          for a worker), a read-only "who is this person". */}
      <DetailDrawer
        open={profileApplicationId !== null}
        onClose={() => setProfileApplicationId(null)}
        labelledBy={APPLICANT_DRAWER_TITLE_ID}
        selectableSelector=".wx-aw-queue-row"
      >
        <ApplicantDetailContent
          titleId={APPLICANT_DRAWER_TITLE_ID}
          state={applicantDetailState}
          onProfileSaved={applicantDetailState.refetch}
        />
      </DetailDrawer>

      {/* Button click -> the e-KYC review action surface. */}
      <DetailDrawer
        open={reviewApplicationId !== null}
        onClose={closeReview}
        labelledBy={REVIEW_DRAWER_TITLE_ID}
        selectableSelector=".wx-aw-row-action"
      >
        <EkycReviewDrawer
          queueRow={reviewRow}
          titleId={REVIEW_DRAWER_TITLE_ID}
          onChanged={() => setReloadToken((token) => token + 1)}
        />
      </DetailDrawer>
    </section>
  );
}

/** The one real next step for an e-KYC queue row. The action is shown ONLY when
 *  the backend says this viewer can actually perform it (nextAction.canAct);
 *  otherwise the row's own status label conveys the state and the whole row
 *  stays clickable to view the detail - so the row can never offer a button the
 *  drawer or the endpoint would then refuse.
 *
 *  P157-R3: CreateWorkerProfile joined the two e-KYC actions here, inherited
 *  verbatim from the deleted "Finalized Worker Profile" tab (same real
 *  POST .../ekyc-reviews/{id}/worker-profile, same busy state, same error copy),
 *  so removing that tab removed a tab and not a capability. A row whose profile
 *  already exists (CompleteProfile) gets no button - it lives in the Ready for
 *  Match tab, and a second door to it here would just be noise. */
function EkycRowAction({ item, onOpen, onCreateProfile, busy }) {
  const action = item.nextAction;
  if (action?.code === "StartEkyc" && action.canAct) {
    return (
      <button type="button" className="wx-aw-row-action" onClick={onOpen}>
        <PlayCircle size={13} aria-hidden="true" />
        เริ่ม e-KYC
      </button>
    );
  }
  if (action?.code === "MakeDecision" && action.canAct) {
    return (
      <button type="button" className="wx-aw-row-action wx-aw-row-action--soft" onClick={onOpen}>
        <UserCheck size={13} aria-hidden="true" />
        ตรวจ e-KYC
      </button>
    );
  }
  if (action?.code === "CreateWorkerProfile" && action.canAct) {
    return (
      <button type="button" className="wx-aw-row-action" onClick={onCreateProfile} disabled={busy}>
        <FilePlus2 size={13} aria-hidden="true" />
        {busy ? "กำลังสร้าง..." : "สร้าง Worker Profile"}
      </button>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tab 2: Ready for Match - worker profiles, fill their detail and send to
// matching (the real mark-ready endpoint, with its readiness gate).
// ---------------------------------------------------------------------------
function ReadyTab({ onCount, copy = VARIANTS.agent }) {
  const { input, setInput, search } = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null });

  useEffect(() => setPage(1), [search]);

  // reloadToken is a dep (not a forwarded field) so bumping it after a
  // mark-ready gives `filters` a fresh identity and the hook re-fetches - the
  // extra key would be dropped by the params whitelist anyway.
  const filters = useMemo(
    () => ({ page, pageSize: READY_FOR_MATCH_DEFAULT_PAGE_SIZE, search }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, search, reloadToken]
  );
  const { status, data, error } = useReadyForMatchCandidatesList(filters);

  useEffect(() => {
    if (!selectedWorkerId) {
      setDetailState({ status: "idle", data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setDetailState({ status: "loading", data: null, error: null });
    getWorkerProfile(selectedWorkerId)
      .then((profile) => {
        if (!cancelled) setDetailState({ status: "ready", data: profile, error: null });
      })
      .catch((err) => {
        if (!cancelled) setDetailState({ status: "error", data: null, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWorkerId, reloadToken]);

  const items = data?.items ?? [];
  const summary = data?.summary;
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / READY_FOR_MATCH_DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (data) onCount?.(totalCount);
  }, [data, totalCount, onCount]);

  async function handleMarkReady(item) {
    setBusyId(item.workerId);
    setActionError(null);
    try {
      await markReadyForMatch(item.workerId);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setActionError(describeActionError(err, "ยังส่งไป matching ไม่ได้ ข้อมูลแรงงานอาจยังไม่ครบ"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="wx-panel wx-aw-list-panel">
      <SearchBar
        input={input}
        onChange={setInput}
        placeholder="ค้นหาชื่อหรือรหัสแรงงาน"
        totalCount={data ? totalCount : null}
        shownCount={items.length}
      />

      <p className="wx-aw-tab-hint">
        คลิกที่แถวไหนก็ได้เพื่อเปิดรายละเอียดของแรงงานคนนั้น (และเข้าไปดู/แก้ไขโปรไฟล์แบบละเอียดได้จากตรงนั้น) กด "ส่งไป matching" เพื่อส่งแรงงานเข้าระบบจับคู่งาน (ระบบจะนำไปชนกับงานจากฝั่งนายจ้าง) ถ้าข้อมูลยังไม่ครบ ระบบจะบอกว่าต้องเติมอะไรบ้าง
      </p>

      {summary && (
        <div className="wx-aw-ready-summary">
          <span>ส่งแล้ว <strong>{summary.readyForMatchCount}</strong></span>
          <span>ยังไม่ส่ง <strong>{summary.notReadyForMatchCount}</strong></span>
        </div>
      )}

      {actionError && <p className="wx-aw-action-error" role="alert">{actionError}</p>}

      {status === "error" && data === null ? (
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <p className="wx-state-body">{describeListError(error)}</p>
        </div>
      ) : status === "loading" && data === null ? (
        <PageSkeleton variant="table" label="กำลังโหลดรายการ..." />
      ) : items.length === 0 ? (
        <p className="wx-empty-list">{copy.readyEmpty}</p>
      ) : (
        <>
          <ul className="wx-aw-queue">
            {items.map((item) => (
              <li
                key={item.workerId}
                className="wx-aw-queue-row wx-aw-queue-row--clickable"
                onClick={() => setSelectedWorkerId(item.workerId)}
              >
                <WorkerRowAvatar applicationId={item.sourceApplicationId} initials={initials(item.fullName)} />
                {/* Kept a real button inside the clickable row for keyboard
                    users - see the KYC tab's row for the same pairing. */}
                <button type="button" className="wx-aw-queue-main" onClick={() => setSelectedWorkerId(item.workerId)}>
                  <span className="wx-aw-queue-name">{item.fullName || "-"}</span>
                  <span className="wx-aw-queue-sub">
                    {item.workerCode}
                    {item.nationality ? ` · ${item.nationality}` : ""}
                  </span>
                  <OwningLine item={item} copy={copy} />
                </button>
                <div className="wx-aw-queue-side" onClick={(event) => event.stopPropagation()}>
                  {item.readyForMatch ? (
                    <span className="wx-aw-row-done">
                      <ShieldCheck size={14} aria-hidden="true" /> ส่งไป matching แล้ว
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="wx-aw-row-action"
                      onClick={() => handleMarkReady(item)}
                      disabled={busyId === item.workerId}
                    >
                      <Send size={14} aria-hidden="true" />
                      {busyId === item.workerId ? "กำลังส่ง..." : "ส่งไป matching"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <DetailDrawer
        open={selectedWorkerId !== null}
        onClose={() => setSelectedWorkerId(null)}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector=".wx-aw-queue-row"
      >
        <WorkerDetailContent
          titleId={DRAWER_TITLE_ID}
          state={detailState}
          onProfileSaved={() => setReloadToken((token) => token + 1)}
        />
      </DetailDrawer>
    </section>
  );
}

/**
 * Prompt 140B-R2: reused verbatim - the worker already has a completed Worker
 * Profile, an even later state than a Quick Registered applicant, so the same
 * "already in the Agent's line" restatement applies.
 */
function WorkerLineMembershipPanel() {
  return (
    <section className="wx-applicant-membership-panel">
      <span className="wx-applicant-membership-heading">{LINE_MEMBERSHIP_COPY.heading}</span>
      <p className="wx-applicant-membership-body">{LINE_MEMBERSHIP_COPY.body}</p>
    </section>
  );
}

/**
 * Prompt 140B-R3: real inline add/edit/revoke buddies for this worker profile.
 * P157: the "จัดการ Buddy ทั้งหมด" bulk link was removed here to match the
 * applicant drawer's own decluttering - buddies are managed inline, and the
 * full network page stays reachable from the sidebar's Buddy Guarantee entry.
 */
function WorkerBuddySection({ workerId, name, pipelineStatus }) {
  const source = { sourceType: "WorkerProfile", workerApplicationId: null, workerProfileId: workerId, name, pipelineStatus };
  return (
    <section className="wx-aw-dsec">
      <h3>Buddy (เพื่อนคู่ดูแล)</h3>
      <BuddyManagerSection source={source} showNetworkChart={false} formPresentation="dialog" />
    </section>
  );
}

/** Same honest "not started yet" tracking status as the applicant drawer. */
function WorkerTrackingSection() {
  return (
    <section className="wx-aw-dsec">
      <h3>Tracking</h3>
      <div className="wx-aw-kv-row">
        <span>สถานะ</span>
        <strong>{describeTrackingStatus()}</strong>
      </div>
    </section>
  );
}

/**
 * P157: the detailed-profile call-to-action inside the worker drawer, mirroring
 * the applicant drawer's own CTA card - filling the detailed profile is the
 * work that unlocks the readiness gate for "ส่งไป matching".
 */
function WorkerDetailedProfileCta({ applicationId, onSaved }) {
  const [open, setOpen] = useState(false);
  if (!applicationId) {
    return null;
  }
  return (
    <section className="wx-aw-dsec">
      <div className="wx-applicant-profile-cta">
        <span className="wx-applicant-profile-cta-icon" aria-hidden="true"><FilePlus2 size={20} /></span>
        <div className="wx-applicant-profile-cta-text">
          <strong>โปรไฟล์แรงงานแบบละเอียด</strong>
          <span>ดูและแก้ไขข้อมูลตัวตน เอกสาร ทักษะ และพื้นที่ที่ทำงาน เพื่อให้พร้อมส่งไปจับคู่งาน</span>
        </div>
        <button type="button" className="wx-button wx-button--primary wx-applicant-profile-cta-button" onClick={() => setOpen(true)}>
          ดูโปรไฟล์แรงงานแบบละเอียด
        </button>
      </div>
      <WorkerDetailedProfileDialog open={open} applicationId={applicationId} onClose={() => setOpen(false)} onSaved={onSaved} />
    </section>
  );
}

function WorkerDetailContent({ titleId, state, onProfileSaved }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-aw-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">ไม่สามารถโหลดรายละเอียดได้</h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;

  return (
    <div className="wx-aw-drawer">
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.worker.fullNameNative || detail.worker.fullNameEnglish || detail.workerCode}
        title={detail.worker.fullNameNative || detail.worker.fullNameEnglish || "-"}
        subtitle={detail.workerCode}
        badges={[
          { label: detail.workerStatus, variant: statusBadgeVariant(detail.workerStatus) },
          { label: detail.complianceStatus, variant: statusBadgeVariant(detail.complianceStatus) },
          detail.readyForMatch ? { label: "ส่งไป matching แล้ว", variant: "success" } : null,
        ]}
      />

      <WorkerLineMembershipPanel />

      <WorkerDetailedProfileCta applicationId={detail.sourceApplicationId} onSaved={onProfileSaved} />

      <section className="wx-aw-dsec">
        <h3>ข้อมูลทั่วไป</h3>
        <div className="wx-aw-kv-row"><span>ชื่อ (ภาษาอังกฤษ)</span><strong>{detail.worker.fullNameEnglish || "-"}</strong></div>
        <div className="wx-aw-kv-row"><span>สัญชาติ</span><strong>{detail.worker.nationality || "-"}</strong></div>
        <div className="wx-aw-kv-row"><span>เบอร์โทร</span><strong>{detail.worker.phoneNumber || "-"}</strong></div>
        <div className="wx-aw-kv-row"><span>ภาษาที่ถนัด</span><strong>{detail.worker.preferredLanguageCode || "-"}</strong></div>
        <div className="wx-aw-kv-row"><span>PLC / บมจ</span><strong>{detail.line.plcName || "-"}</strong></div>
      </section>

      <section className="wx-aw-dsec">
        <h3>ผู้ติดต่อ ({detail.contacts.length})</h3>
        {detail.contacts.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มีข้อมูลผู้ติดต่อ</p>
        ) : (
          detail.contacts.map((contact, index) => (
            <div className="wx-aw-kv-row" key={index}>
              <span>{contact.contactType}{contact.isPrimary ? " (หลัก)" : ""}</span>
              <strong>{contact.contactValue}</strong>
            </div>
          ))
        )}
      </section>

      <section className="wx-aw-dsec">
        <h3>ทักษะ ({detail.skills.length})</h3>
        {detail.skills.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มีข้อมูลทักษะ</p>
        ) : (
          detail.skills.map((skill, index) => (
            <div className="wx-aw-kv-row" key={index}>
              <span>{skill.skillName || skill.skillCode}</span>
              <strong>{skill.proficiencyLevel || "-"}</strong>
            </div>
          ))
        )}
      </section>

      <section className="wx-aw-dsec">
        <h3>เอกสาร ({detail.documents.length})</h3>
        {detail.documents.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มีข้อมูลเอกสาร</p>
        ) : (
          detail.documents.map((document, index) => (
            <div className="wx-aw-kv-row" key={index}>
              <span>{document.documentType}</span>
              <strong>{document.documentStatus}</strong>
            </div>
          ))
        )}
      </section>

      {detail.sourceApplicationId && (
        <section className="wx-aw-dsec">
          <h3>แหล่งที่มา</h3>
          <div className="wx-aw-kv-row">
            <span>Worker Application ID</span>
            <strong>{detail.sourceApplicationId}</strong>
          </div>
        </section>
      )}

      <WorkerTrackingSection />

      <WorkerBuddySection
        workerId={detail.workerId}
        name={detail.worker.fullNameNative || detail.worker.fullNameEnglish || "-"}
        pipelineStatus={detail.workerStatus}
      />
    </div>
  );
}
